import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Grid,
  Card,
  Container,
  CardContent,
  Typography,
  Badge,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Link, useNavigate } from "react-router-dom";
import { Search, UserPlus, X } from "lucide-react";
import { MoreVert } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { NURSING_LIST } from "../_mock/mock";
import { EyeIcon, FileIcon } from "../comps/CustomIcons";
import MedicineRequestModal from "../components/nursing/modals/medicine-request-modal";
import AddSurgeryModal from "../components/nursing/modals/add-surgery-modal";
import NursingAssessmentModal from "../components/nursing/modals/nursing-assessment-modal";
import RequestInvestigationModal from "../components/nursing/modals/request-investigation-modal";
import DoctorVisitModal from "../components/nursing/modals/doctor-visit-charges";
import InitialDischargeDetailsModal from "../components/nursing/modals/initial-discharge-details-modal";
import ConfirmDischargeModal from "../components/nursing/modals/confirm-discharge-modal";
import BedTransferModal from "../components/nursing/modals/bed-transfer-modal";
import BedDislocationModal from "../components/nursing/modals/bed-dislocation-modal";
import InitiateUnitTransferModal from "../components/nursing/modals/initiate-unit-transfer-modal";
import AddNoteModal from "../components/nursing/modals/add-note-modal";
import { useGetApi } from "../hooks/useGetApi";

import { getWardApi } from "../services/ward.service";
import {
  getNursingBedNumberApi,
  updateBedNumberApi,
} from "../services/bed-number.service";

import ConfirmationDialog from "../components/confirmation-dialog/confirmation-dialog";
import { toast } from "react-toastify";

import {
  CAPITALIZE,
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import dayjs from "dayjs";

const headLabel = [
  {
    id: 1,
    label: "Ward Name",
    value: "wardName",
  },
  {
    id: 2,
    label: "Bed Number",
    value: "bedNumber",
  },
  {
    id: 3,
    label: "UHID",
    value: "uhid",
  },
  {
    id: 4,
    label: "Patient Name",
    value: "patientName",
  },
  {
    id: 5,
    label: "Age",
    value: "age",
  },
  {
    id: 6,
    label: "Gender",
    value: "gender",
  },
  {
    id: 7,
    label: "Admission Date",
    value: "admissionDate",
  },
  {
    id: 8,
    label: "Doctor Name",
    value: "doctorName",
  },
  {
    id: 9,
    label: "Bed Status",
    value: "bedStatus",
  },
  {
    id: 10,
    label: "",
    value: "",
    align: "right",
  },
];

const popoverOptions = [
  "Patient Details",
  "Request Medicine",
  "Medicine Request Tracking",
  "Return Medicine",
  "Add Surgery/Procedure",
  "Nursing Assessment",
  "Request Investigation",
  "Doctors Visits",
  "Initiate/Manage Discharge",
  "Discharge Summary",
  "Confirm Discharge",
  "Bed History",
  "Bed Transfer",
  "Bed Dislocation",
  "Unit Transfer",
  "Unit Transfer History",
  "Bill",
  "Note",
  "Bed Under Repair",
  "Ready To Use",
  "Bed Allocation",
];

const buttons = [
  { label: "All Beds", value: "", width: "130px" },
  { label: "Occupied", value: "Occupied", width: "140px" },
  { label: "Ready to use", value: "Available", width: "160px" },
  { label: "Bed Under Repair", value: "Under Maintenance", width: "190px" },
];

const Nursing = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeButton, setActiveButton] = useState("All Beds");
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("UHID");
  const [ward, setWard] = useState("");
  const [status, setStatus] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [medicineRequestOpen, setMedicineRequestOpen] = useState(false);
  const [surgeryModalOpen, setSurgeryModalOpen] = useState(false);
  const [nursingAssessmentModalOpen, setNursingAssessmentModalOpen] =
    useState(false);
  const [requestInvestigationModalOpen, setRequestInvestigationModalOpen] =
    useState(false);
  const [doctorVisitModalOpen, setDoctorVisitModalOpen] = useState(false);
  const [
    initialDischargeDetailsModalOpen,
    setInitialDischargeDetailsModalOpen,
  ] = useState(false);
  const [confirmDischargeModalOpen, setConfirmDischargeModalOpen] =
    useState(false);
  const [bedTransferModalOpen, setBedTransferModalOpen] = useState(false);
  const [bedDislocationModalOpen, setBedDislocationModalOpen] = useState(false);
  const [initiateUnitTransferModalOpen, setInitiateUnitTransferModalOpen] =
    useState(false);
  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [requestType, setRequestType] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch all nursing beds for client-side sorting and pagination (like Consultation page)
  const {
    dataList: nursingList,
    dataCount: nursingCount,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getNursingBedNumberApi,
    params: {
      limit: 10000, // Fetch large number for client-side pagination/sorting
      page: 1,
      search,
      searchBy,
      bedStatus: status,
      wardName: ward,
      // Remove sort/order - will be handled client-side
    },
    dependencies: [search, searchBy, status, ward],
    // Remove paginationModel and sortModel from dependencies - handled client-side
    debounceDelay: 500,
    dataKey: "bedNumbers",
    countKey: "total",
  });

  // Use the nursing list directly without patching to preserve API filtering
  const patchedNursingList = nursingList || [];

  // Prepare rows for DataGrid with unique IDs
  const rows = patchedNursingList?.map((row, index) => {
    // Ensure unique ID by combining _id with index to handle any potential duplicates
    const uniqueId = row._id ? `${row._id}-${index}` : `row-${index}`;
    return {
      id: uniqueId,
      _id: row._id,
      wardName: row?.wardName || "-",
      bedNumber: row?.bedNumber || "-",
      UHID: row?.occupancyDetails?.UHID || "-",
      patientName: row?.occupancyDetails?.patientName || "-",
      patientId: row?.occupancyDetails?.patientId,
      age: Math.floor(row?.occupancyDetails?.age) || "-",
      gender: CAPITALIZE(row?.occupancyDetails?.gender) || "-",
      admissionDate: row?.occupancyDetails?.admissionDate,
      doctorName: row?.occupancyDetails?.doctorName || "-",
      bedStatus: row?.actualStatus || row?.status || "-",
      occupancyDetails: row?.occupancyDetails,
      actualStatus: row?.actualStatus,
      status: row?.status,
    };
  }) || [];

  // Compute status counts when viewing all beds
  const statusCountsRef = useRef({});
  useMemo(() => {
    if (status === "" && patchedNursingList?.length > 0) {
      const counts = { "": patchedNursingList.length };
      patchedNursingList.forEach((row) => {
        const s = row?.actualStatus || row?.status;
        if (s) counts[s] = (counts[s] || 0) + 1;
      });
      statusCountsRef.current = counts;
    }
  }, [status, patchedNursingList]);

  // Responsive columns configuration
  const getColumns = useCallback(() => {
    const baseColumns = [
      {
        field: 'wardName',
        headerName: 'Ward Name',
        flex: isMobile ? 0 : 1,
        width: isMobile ? 100 : undefined,
        minWidth: 100,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'bedNumber',
        headerName: 'Bed Number',
        flex: isMobile ? 0 : 0.8,
        width: isMobile ? 90 : undefined,
        minWidth: 90,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'UHID',
        headerName: 'UHID',
        flex: isMobile ? 0 : 0.8,
        width: isMobile ? 100 : undefined,
        minWidth: 90,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'patientName',
        headerName: 'Patient Name',
        flex: isMobile ? 0 : 1.2,
        width: isMobile ? 120 : undefined,
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        renderCell: (params) => {
          if (params.row.patientId) {
            return (
              <Box
                component={Link}
                to={`/patient/info/${params.row.patientId}`}
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  textDecoration: params.value ? "underline" : "none",
                  fontSize: isMobile ? '12px' : '14px',
                }}
              >
                {params.value || "-"}
              </Box>
            );
          }
          return <span>-</span>;
        },
      },
      {
        field: 'age',
        headerName: 'Age',
        flex: isMobile ? 0 : 0.5,
        width: isMobile ? 60 : undefined,
        minWidth: 50,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'gender',
        headerName: 'Gender',
        flex: isTablet ? 0 : 0.7,
        width: isTablet ? 80 : undefined,
        minWidth: 70,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'admissionDate',
        headerName: 'Admission Date',
        flex: isTablet ? 0 : 1,
        width: isTablet ? 120 : undefined,
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        valueFormatter: (params) => {
          return params.value ? dayjs(params.value).format("YYYY-MM-DD") : "-";
        },
      },
      {
        field: 'doctorName',
        headerName: 'Doctor Name',
        flex: isTablet ? 0 : 1,
        width: isTablet ? 120 : undefined,
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'bedStatus',
        headerName: 'Bed Status',
        flex: isTablet ? 0 : 0.8,
        width: isTablet ? 100 : undefined,
        minWidth: 90,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0,
        width: 70,
        minWidth: 60,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: (params) => (
          <IconButton
            aria-label="actions"
            size={isMobile ? "small" : "medium"}
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e, params.row);
            }}
          >
            <MoreVert fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        ),
      },
    ];

    // Responsive column hiding
    if (isMobile) {
      return baseColumns.filter(col => 
        ['wardName', 'bedNumber', 'patientName', 'actions'].includes(col.field)
      );
    }
    if (isTablet) {
      return baseColumns.filter(col => 
        ['wardName', 'bedNumber', 'UHID', 'patientName', 'admissionDate', 'bedStatus', 'actions'].includes(col.field)
      );
    }
    return baseColumns;
  }, [isMobile, isTablet]);

  const handleRightClick = (event, row) => {
    event.preventDefault(); // Prevent the default context menu
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleConfirmationModalOpen = (currentRequestType) => {
    const bedStatus = selectedRow?.actualStatus || selectedRow?.status;
    const isBedOccupied = bedStatus === "Occupied";
    const isBedAvailable = bedStatus === "Available";
    const isBedUnderMaintenance = bedStatus === "Under Maintenance";

    if (isBedOccupied) {
      toast.error("Cannot perform action since bed is occupied.");
      return;
    }

    if (isBedUnderMaintenance && currentRequestType === "Bed Under Repair") {
      toast.error("Cannot perform action as bed is already under maintenance");
      return;
    }

    if (isBedAvailable && currentRequestType === "Ready To Use") {
      toast.error(`Cannot perform action as bed is already available.`);
      return;
    }

    setRequestType(currentRequestType);
    setConfirmationModalOpen(true);
  };

  const handleConfirmationModalClose = () => {
    setConfirmationModalOpen(false);
    setSelectedRow(null);
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };
  const { dataList: wardList } = useGetApi({
    apiFunction: getWardApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "wards",
  });

  //bed status update function
  const bedStatusUpdate = async () => {
    let response;
    if (selectedRow?._id) {
      setLoading(true);
      response = await updateBedNumberApi({
        _id: selectedRow?._id,
        status:
          requestType === "Bed Under Repair"
            ? "Under Maintenance"
            : "Available",
      });
      setLoading(false);
    }

    if (response?.code === 200 || response?.code === 201) {
      toast.success(
        `Status of Bed Number ${selectedRow?.bedNumber} updated successfully`
      );
      handleConfirmationModalClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  const handleModalOpen = (index) => {
    handleClose();
    switch (index) {
      case 0:
        if (selectedRow?.occupancyDetails?.patientId) {
          navigate(`/patient/info/${selectedRow?.occupancyDetails?.patientId}`);
        } else {
          toast.error("No patient assigned to this bed.");
        }
        break;
      case 1:
        setMedicineRequestOpen(true);
        break;
      case 2:
        navigate(
          `/nursing/medicine-requests-tracking/${selectedRow?.occupancyDetails?.patientId}/${selectedRow?.occupancyDetails?.visitId}`
        );
        break;
      case 3:
        navigate(
          `/nursing/medicine-return/${selectedRow?.occupancyDetails?.patientId}/${selectedRow?.occupancyDetails?.visitId}`
        );
        break;
      case 4:
        setSurgeryModalOpen(true);
        break;
      case 5:
        setNursingAssessmentModalOpen(true);
        break;
      case 6:
        setRequestInvestigationModalOpen(true);
        break;
      case 7:
        setDoctorVisitModalOpen(true);
        break;
      case 8:
        setInitialDischargeDetailsModalOpen(true);
        break;
      case 9:
        navigate(
          `/nursing/discharge-summary/${selectedRow?.occupancyDetails?.visitId}`
        );
        break;
      case 10:
        setConfirmDischargeModalOpen(true);
        break;
      case 11:
        navigate(
          `/nursing/bed-history/${selectedRow?.occupancyDetails?.visitId}`
        );
        break;
      case 12:
        setBedTransferModalOpen(true);
        break;
      case 13:
        setBedDislocationModalOpen(true);
        break;
      case 14:
        setInitiateUnitTransferModalOpen(true);
        break;
      case 15:
        navigate(
          `/nursing/unit-transfer-history/${selectedRow?.occupancyDetails?.visitId}`
        );
        break;
      case 16:
        navigate(
          `/billing/${selectedRow?.occupancyDetails?.patientId}`
        );
      case 17:
        setAddNoteModalOpen(true);
        break;
      case 18:
        handleConfirmationModalOpen("Bed Under Repair");
        break;
      case 19:
        handleConfirmationModalOpen("Ready To Use");
        break;
      default:
        break;
    }
  };
  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Search */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "20px",
              marginBottom: "40px",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Dropdown - only show when search is focused */}
              {isSearchFocused && (
                <select
                  onChange={(e) => setSearchBy(e.target.value)}
                  value={searchBy}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    padding: "12px 16px",
                    fontSize: "14px",
                    width: `${Math.max(120, (searchBy === "UHID" ? "UHID" : 
                      searchBy === "firstName" ? "Patient's First Name" : 
                      searchBy === "contactNo" ? "Contact Number" : "UHID").length * 9 + 30)}px`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    borderRight: "1px solid #e0e0e0",
                    color: "#333",
                    fontWeight: "500",
                    touchAction: "manipulation"
                  }}
                >
                  <option value="UHID">UHID</option>
                  <option value="firstName">Patient's First Name</option>
                  <option value="contactNo">Contact Number</option>
                </select>
              )}
              
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder={`Search by ${searchBy === "UHID" ? "UHID" : 
                    searchBy === "firstName" ? "Patient's First Name" : 
                    searchBy === "contactNo" ? "Contact Number" : "UHID"}`}
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value || "")}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography>Actions:</Typography>
              <Link to="/nursing/approvals">
                <Tooltip title="Ward Transfer Approval" arrow>
                  <IconButton
                    sx={{
                      bgcolor: "#F0F7FC",
                      borderRadius: "8px",
                      height: "45px",
                      width: "45px",
                    }}
                  >
                    <FileIcon sx={{ color: "black" }} />
                  </IconButton>
                </Tooltip>
              </Link>
              <Link to="/nursing/all-medicine-trackings">
                <Tooltip title="All Medication Tracking" arrow>
                  <IconButton
                    sx={{
                      bgcolor: "#F0F7FC",
                      borderRadius: "8px",
                      height: "45px",
                      width: "45px",
                    }}
                  >
                    <EyeIcon sx={{ color: "black" }} />
                  </IconButton>
                </Tooltip>
              </Link>
            </Box>
          </Box>

          {/* Filter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "40px",
            }}
          >
            <FormControl size="small" sx={{ minWidth: "150px" }}>
              <InputLabel id="unit-label">Ward Name</InputLabel>
              <Select
                labelId="unit-label"
                label="Ward Name"
                value={ward || ""}
                onChange={(e) => {
                  setWard(e.target.value);
                  setPaginationModel({ page: 0, pageSize: paginationModel.pageSize }); // Reset to first page when filter changes
                }}
              >
                <MenuItem value="">--NA--</MenuItem>
                {wardList?.map((option) => (
                  <MenuItem value={option?.wardName} key={option?._id}>
                    {option?.wardName || ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {buttons?.map((button) => (
                <Button
                  key={button?.label}
                  variant="outlined"
                  onClick={() => {
                    setActiveButton(button?.label);
                    setStatus(button?.value);
                    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize }); // Reset to first page when filter changes
                  }}
                  sx={{
                    border: `2px solid ${
                      activeButton === button?.label
                        ? theme.palette.primary.dark
                        : "#c0c0c0"
                    }`,
                    color:
                      activeButton === button?.label
                        ? theme.palette.primary.dark
                        : "text.secondary",
                    bgcolor:
                      activeButton === button?.label ? "#AED5F3" : "#F0F7FC",
                    fontWeight: activeButton === button?.label ? 600 : 400,
                    width: button?.width || "100px",
                    whiteSpace: "nowrap",
                    textTransform: "none",
                  }}
                >
                  {button?.label || ""}{statusCountsRef.current[button?.value] !== undefined ? ` (${statusCountsRef.current[button?.value]})` : ""}
                </Button>
              ))}
            </Box>
          </Box>

          {/* DataGrid Table */}
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <>
              <Box sx={{ 
                height: { 
                  xs: 'calc(100vh - 300px)', 
                  sm: 'calc(100vh - 320px)', 
                  md: 'calc(100vh - 350px)' 
                },
                width: '100%',
                minHeight: '300px',
                maxWidth: '100%',
                overflow: 'hidden'
              }}>
                <DataGrid
                  rows={rows}
                  columns={getColumns()}
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationModelChange}
                  pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                  paginationMode="client"
                  sortingMode="client"
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                  disableRowSelectionOnClick
                  loading={isLoading}
                  onCellClick={(params, event) => {
                    if (params.field !== 'actions') {
                      handleClick(event, params.row);
                    }
                  }}
                  onRowContextMenu={(params, event) => {
                    event.preventDefault();
                    handleRightClick(event, params.row);
                  }}
                  componentsProps={{
                    row: {
                      onContextMenu: (event) => {
                        event.preventDefault();
                      },
                    },
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                      width: '100%',
                    },
                    '& .MuiDataGrid-main': {
                      width: '100%',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #f0f0f0',
                      padding: isMobile ? '4px' : '8px',
                      fontSize: isMobile ? '12px' : '14px',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '12px' : '14px',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                      fontSize: isMobile ? '12px' : '14px',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(odd)': {
                        backgroundColor: '#fafafa',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'white',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#f9f9f9',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      minHeight: '200px',
                    },
                    [theme.breakpoints.down('sm')]: {
                      '& .MuiDataGrid-columnHeader': {
                        fontSize: '11px',
                        padding: '4px',
                      },
                      '& .MuiDataGrid-cell': {
                        fontSize: '11px',
                        padding: '2px 4px',
                      },
                    },
                  }}
                  components={{
                    NoRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          No beds found
                        </Typography>
                      </Box>
                    ),
                  }}
                />
              </Box>

              {/* Pop-up Menu */}
              <Menu
                id={Boolean(anchorEl) ? "row-popover" : undefined}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
                anchorPosition={
                  anchorPosition
                    ? { top: anchorPosition.top, left: anchorPosition.left }
                    : undefined
                }
                anchorOrigin={{
                  vertical: "center",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "8px",
                      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                {popoverOptions?.map((option, index) => (
                  <MenuItem key={index} onClick={() => handleModalOpen(index)}>
                    {option || ""}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          <MedicineRequestModal
            open={medicineRequestOpen}
            onClose={() => {
              setMedicineRequestOpen(false);
              setSelectedRow(null);
            }}
            selectedRow={selectedRow}
          />
          <AddSurgeryModal
            open={surgeryModalOpen}
            id={selectedRow?.occupancyDetails?.visitId}
            onClose={() => {
              setSurgeryModalOpen(false);
              setSelectedRow(null);
            }}
          />
          <NursingAssessmentModal
            open={nursingAssessmentModalOpen}
            selectedRow={selectedRow}
            onClose={() => {
              setNursingAssessmentModalOpen(false);
              setSelectedRow(null);
            }}
          />
          <RequestInvestigationModal
            open={requestInvestigationModalOpen}
            selectedRow={selectedRow}
            onClose={() => {
              setRequestInvestigationModalOpen(false);
              setSelectedRow(null);
            }}
          />
          <DoctorVisitModal
            open={doctorVisitModalOpen}
            id={selectedRow?.occupancyDetails?.visitId}
            onClose={() => {
              setDoctorVisitModalOpen(false);
              setSelectedRow(null);
            }}
          />
          <InitialDischargeDetailsModal
            open={initialDischargeDetailsModalOpen}
            id={selectedRow?.occupancyDetails?.visitId}
            onClose={() => {
              setInitialDischargeDetailsModalOpen(false);
              setSelectedRow(null);
            }}
          />
          <ConfirmDischargeModal
            selectedRow={selectedRow}
            open={confirmDischargeModalOpen}
            onClose={() => {
              setConfirmDischargeModalOpen(false);
              setSelectedRow(null);
            }}
          />
          <BedTransferModal
            open={bedTransferModalOpen}
            onClose={() => {
              setBedTransferModalOpen(false);
              setSelectedRow(null);
            }}
            selectedRow={selectedRow}
            refetch={refetch}
          />
          <BedDislocationModal
            open={bedDislocationModalOpen}
            id={selectedRow?.occupancyDetails?.visitId}
            onClose={() => {
              setBedDislocationModalOpen(false);
              setSelectedRow(null);
            }}
          />
          <InitiateUnitTransferModal
            open={initiateUnitTransferModalOpen}
            onClose={() => {
              setInitiateUnitTransferModalOpen(false);
              setSelectedRow(null);
            }}
            selectedRow={selectedRow}
            refetch={refetch}
          />
          <AddNoteModal
            open={addNoteModalOpen}
            id={selectedRow?.occupancyDetails?.visitId}
            onClose={() => {
              setAddNoteModalOpen(false);
              setSelectedRow(null);
            }}
          />
          <ConfirmationDialog
            open={confirmationModalOpen}
            onCancel={handleConfirmationModalClose}
            onConfirm={bedStatusUpdate}
            isLoading={loading}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default Nursing;
